/**
 * Web App Root Component
 *
 * Provides routing and authentication for the web application.
 */

import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import { PlatformProvider } from './contexts/platform-context';
import { useClickTracker } from './hooks/use-click-tracker';
import { AuthCallbackPage } from './pages/auth-callback';
import { ChatPage } from './pages/chat';
import { GuestChatPage } from './pages/guest-chat';
import { PrivacyPage } from './pages/privacy';
import { SignInPage } from './pages/signin';
import { SupportPage } from './pages/support';
import { ThemeProvider } from './styles/theme-provider';
import { ToastProvider } from './components/toast-context';
import { ToastContainer } from './components/toast-container';

// Internal Ops Console — lazy-loaded, unobvious same-origin path. The server-side allowlist
// (withInternalAdmin + INTERNAL_ADMIN_EMAILS) is the real gate; the path is "unlisted", not a secret.
import { OPS_CONSOLE_PATH } from './config/ops';
const OpsConsolePage = React.lazy(() => import('./pages/ops-console'));

// Create query client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * Protected Route Component
 * Redirects to signin if not authenticated
 * Preserves query parameters (e.g., ?context=sidebar)
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve query parameters when redirecting to signin
    const search = window.location.search;
    return <Navigate to={`/signin${search}`} replace />;
  }

  return <>{children}</>;
};

/**
 * App Component with Providers
 */
export const App = () => {
  // Enable global click tracking for feedback system
  useClickTracker();

  return (
    <div data-app-root className="platform-web w-full h-screen overflow-hidden">
      <QueryClientProvider client={queryClient}>
        <PlatformProvider>
          <ThemeProvider>
            <ToastProvider>
              <BrowserRouter>
                <AuthProvider>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/signin" element={<SignInPageWrapper />} />
                    <Route path="/auth/callback" element={<AuthCallbackPage />} />
                    <Route
                      path={OPS_CONSOLE_PATH}
                      element={
                        <React.Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
                          <OpsConsolePage />
                        </React.Suspense>
                      }
                    />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/support" element={<SupportPage />} />
                    <Route path="/try" element={<GuestChatPage />} />

                    {/* Protected routes */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <ChatPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Settings deep link (e.g. /settings?panel=admin from the
                        extension sidebar). Renders the same ChatPage, which reads
                        the `panel` query param to open the right settings section. */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <ChatPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch all - redirect to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </AuthProvider>
              </BrowserRouter>
              <div className="fixed bottom-4 left-0 right-0 flex justify-center z-[9999] pointer-events-none">
                <div className="pointer-events-auto w-80 max-w-[90vw]">
                  <ToastContainer />
                </div>
              </div>
            </ToastProvider>
          </ThemeProvider>
        </PlatformProvider>
      </QueryClientProvider>
    </div>
  );
};

/**
 * Sign In Page Wrapper
 * Redirects to home if already authenticated
 * Supports auto-login via URL parameter (e.g., ?autoLogin=true)
 */
const SignInPageWrapper: React.FC = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [hasAutoLoginTriggered, setHasAutoLoginTriggered] = React.useState(false);

  // Check for auto-login parameter and trigger login
  // But NOT if we're in the sidebar (sidebar has its own button to open web app)
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated && !hasAutoLoginTriggered) {
      const params = new URLSearchParams(window.location.search);
      const autoLogin = params.get('autoLogin');
      const context = params.get('context');

      // Only auto-login if explicitly requested AND not in sidebar
      if (autoLogin === 'true' && context !== 'sidebar') {
        console.log('🔐 [SignInPageWrapper] Auto-login triggered');
        setHasAutoLoginTriggered(true);
        // Small delay to ensure UI is ready
        setTimeout(() => {
          login();
        }, 100);
      }
    }
  }, [isLoading, isAuthenticated, hasAutoLoginTriggered, login]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex items-start sm:items-center justify-center overflow-x-hidden w-full">
      <SignInPage onSignIn={login} isLoading={isLoading} className="max-w-6xl w-full" />
    </div>
  );
};
